import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { HiChevronDown, HiEye, HiOutlineDocumentText, HiTrash } from 'react-icons/hi';
import { MdOutlineDocumentScanner, MdBugReport } from 'react-icons/md';
import { motion, AnimatePresence } from 'framer-motion';
import ComponentWrapper from '../components/utils/ComponentWrapper';
import { useAppSelector, useAppDispatch } from '../redux/hooks';
import { fetchInvoiceById, updateInvoice } from '../redux/actions/invoiceActions';
import { fetchCustomers, fetchCustomerProjects, searchCustomers } from '../redux/actions/customerActions';
import { clearCustomerProjects } from '../redux/slices/customerSlice';
import toast from 'react-hot-toast';
import { generateInvoicePDF } from '../utils/pdfGenerator';
import { determineTaxType, getStateFromGSTIN } from '../utils/gstHelper';

interface BilledCompany {
  id: string;
  name: string;
  logo: string;
  addressLines: string[];
  email: string;
  phone: string;
  gstin: string;
  cin: string;
}

interface ProjectItem {
  id: string;
  projectName: string;
  projectNumber: string;
  manakProjectId?: string;
  quantity: number;
  rate: number;
  hsnSac: string;
  paymentReceived: boolean;
}

interface InvoiceUpdatePayload {
  invoiceDate: string;
  term?: string;
  dueDate?: string;
  purchaseOrder?: string;
  discount: number;
  tax?: number;
  cgst?: number;
  sgst?: number;
  amountPaid: number;
  notes?: string;
  termsAndConditions?: string;
  billedToCustomerId?: string; // Add this to allow customer changes
  items: {
    projectId: string;
    quantity: number;
    hsnSac?: string;
    paymentReceived?: boolean;
  }[];
}

const EditInvoice: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const { user } = useAppSelector(state => state.auth);

  const billedCompanies: BilledCompany[] = [
    {
      id: 'mess',
      name: 'Manak Engineering and Software Solutions',
      logo: 'https://d9hz6473ndr7q.cloudfront.net/manak-logo.png',
      addressLines: [
        'E 71, DGS Society Plot -6. Sector -22, Dwarka',
        'New Delhi, Delhi - 110077, India',
      ],
      email: 'info@manakengineering.com',
      phone: '+91 9871698842',
      gstin: '07AFBPJ6861Q1ZV',
      cin: '',
    },
    {
      id: 'mespl',
      name: 'Manak Engineering Services Pvt Ltd',
      logo: 'https://d9hz6473ndr7q.cloudfront.net/manak-logo.png',
      addressLines: [
        'E-71, DGS Society , Plot -6, Sector -22, Dwarka',
        'New Delhi, Delhi - 110077, India',
      ],
      email: 'info@manakengineering.com',
      phone: '+91 9871698842',
      gstin: '07AAQCM7695A1Z1',
      cin: 'U71100DL2023PTC412812',
    },
  ];

  // Form state
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
  const [term, setTerm] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [purchaseOrder, setPurchaseOrder] = useState('');
  const [selectedProjects, setSelectedProjects] = useState<ProjectItem[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [invoiceSeries, setInvoiceSeries] = useState<'MESPL' | 'MESS'>('MESPL');
  const [initialPaymentReceivedProjects, setInitialPaymentReceivedProjects] = useState<Set<string>>(new Set());
  const [originalCustomerId, setOriginalCustomerId] = useState<string>(''); // Track original customer for change detection

  // Customer selection state
  const [selectedCustomer, setSelectedCustomer] = useState<string>('');
  const [selectedCustomerFullData, setSelectedCustomerFullData] = useState<any>(null); // Store full customer data
  const [showCustomerList, setShowCustomerList] = useState(false);
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [isSearchingCustomers, setIsSearchingCustomers] = useState(false);
  const [customerPage, setCustomerPage] = useState(1);
  const [isLoadingMoreCustomers, setIsLoadingMoreCustomers] = useState(false);
  const [hasMoreCustomers, setHasMoreCustomers] = useState(true);
  const customerDropdownRef = useRef<HTMLDivElement>(null);
  const customerScrollRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Customer change confirmation modal state
  const [showCustomerChangeModal, setShowCustomerChangeModal] = useState(false);
  const [pendingCustomerId, setPendingCustomerId] = useState<string>('');
  const [pendingCustomerData, setPendingCustomerData] = useState<any>(null); // Store pending customer data for modal

  // Project removal confirmation modal state (Super Admin only)
  const [showProjectRemovalModal, setShowProjectRemovalModal] = useState(false);
  const [pendingProjectRemoval, setPendingProjectRemoval] = useState<ProjectItem | null>(null);

  // Project selection state
  const [showProjectList, setShowProjectList] = useState(false);
  const [projectPage, setProjectPage] = useState(1);
  const [isLoadingMoreProjects, setIsLoadingMoreProjects] = useState(false);
  const [hasMoreProjects, setHasMoreProjects] = useState(true);
  const projectDropdownRef = useRef<HTMLDivElement>(null);
  const projectSelectorWrapperRef = useRef<HTMLDivElement>(null);
  const projectDropdownPanelRef = useRef<HTMLDivElement>(null);

  // Invoice state from Redux
  const { loading: invoiceLoading, currentInvoice } = useAppSelector(state => state.invoice);
  
  // Customer state from Redux
  const {
    customers,
    searchResults,
    selectedCustomerProjects: customerProjects,
    loading: customerLoading,
    projectsLoading,
    searching,
    pagination: customerPagination
  } = useAppSelector(state => state.customers);

  // Check if user is super admin
  const normalizedDesignation =
    (typeof user?.designation === 'string' ? user.designation : user?.designation?.name || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '');
  const normalizedRole = (user?.role || '').trim().toUpperCase();
  const normalizedUserType = (user?.userType || '').trim().toUpperCase();
  const isSuperAdmin =
    user?.isSuperAdmin === true ||
    normalizedDesignation === 'superadmin' ||
    normalizedRole === 'ADMIN' ||
    normalizedUserType === 'ADMIN';

  // State for amount paid
  const [taxPercentage, setTaxPercentage] = useState<number>(18);
  const [taxType, setTaxType] = useState<'IGST' | 'CGST_SGST'>('IGST');
  const [amountPaid, setAmountPaid] = useState<number>(0);
  const [notes, setNotes] = useState<string>('');
  const [termsConditions, setTermsConditions] = useState<string>('');

  // Payment terms options
  const termOptions = [
    { value: '0', label: 'Due on Receipt' },
    { value: '15', label: 'Net 15' },
    { value: '30', label: 'Net 30' },
    { value: '45', label: 'Net 45' },
    { value: '60', label: 'Net 60' },
    { value: '90', label: 'Net 90' },
  ];

  // Fetch invoice data when component mounts
  useEffect(() => {
    if (invoiceId) {
      console.log('🔍 Fetching invoice with ID:', invoiceId);
      dispatch(fetchInvoiceById(invoiceId))
        .unwrap()
        .then((response) => {
          console.log('📦 Raw API Response:', response);

          // The response structure is { success: true, data: {...} }
          const invoice = response.data || response;
          console.log('📋 Invoice data:', invoice);
          console.log('📦 Invoice items:', invoice.items);
          console.log('📊 Items count:', invoice.items?.length || 0);

          // Set company based on invoice series
          const series = invoice.invoiceSeries || 'MESPL';
          setInvoiceSeries(series as 'MESPL' | 'MESS');
          setSelectedCompanyId(series === 'MESS' ? 'mess' : 'mespl');

          // Populate form fields
          setInvoiceDate(invoice.invoiceDate?.split('T')[0] || new Date().toISOString().split('T')[0]);
          setTerm(invoice.term?.toString() || '');
          setDueDate(invoice.dueDate?.split('T')[0] || '');
          setPurchaseOrder(invoice.purchaseOrder || '');

          // Determine tax type - tax percentage is always 18% (same as CreateInvoice)
          if (invoice.cgst && invoice.sgst) {
            setTaxType('CGST_SGST');
          } else if (invoice.tax) {
            setTaxType('IGST');
          }
          // Tax percentage is fixed at 18% to avoid rounding errors from reverse-calculation
          setTaxPercentage(18);

          setAmountPaid(invoice.amountPaid || 0);
          setNotes(invoice.notes || '');
          setTermsConditions(invoice.termsAndConditions || '');

          // Set selected customer and store original for change detection
          if (invoice.billedToCustomerId) {
            setSelectedCustomer(invoice.billedToCustomerId);
            setOriginalCustomerId(invoice.billedToCustomerId);
          }

          // Set items/projects with proper structure
          console.log('🔄 Processing invoice items...');
          if (invoice.items && invoice.items.length > 0) {
            console.log('✅ Found items:', invoice.items.length);
            const paymentReceivedSet = new Set<string>();
            const projectsWithDefaults: ProjectItem[] = invoice.items.map((item, index: number) => {
              console.log(`  📌 Item ${index + 1}:`, {
                id: item.projectId || item.id,
                projectName: item.project?.projectName,
                projectNumber: item.project?.projectNumber || item.project?.manakProjectId,
                quantity: item.quantity,
                rate: item.rate,
                hsnSac: item.hsnSac
              });

              // Check if project has PAYMENT_RECEIVED status
              const latestStatus = item.project?.projectAssigned?.[0]?.status?.toUpperCase();
              const isPaymentReceived = latestStatus === 'PAYMENT_RECEIVED';

              if (isPaymentReceived) {
                paymentReceivedSet.add(item.projectId || item.id);
              }

              return {
                id: item.projectId || item.id,
                projectName: item.project?.projectName || 'Project',
                projectNumber: item.project?.projectNumber || item.project?.manakProjectId || '',
                manakProjectId: item.project?.manakProjectId || '',
                quantity: item.quantity || 1,
                rate: item.rate || 0,
                hsnSac: item.hsnSac || '',
                paymentReceived: isPaymentReceived
              };
            });
            console.log('✅ Processed projects:', projectsWithDefaults);
            setSelectedProjects(projectsWithDefaults);
            setInitialPaymentReceivedProjects(paymentReceivedSet);
          } else {
            console.warn('⚠️ No items found in invoice');
          }
        })
        .catch((error) => {
          console.error('❌ Error fetching invoice:', error);
          toast.error('Failed to load invoice');
          navigate('/billing-history');
        });
    }
  }, [invoiceId, dispatch, navigate]);

  // Calculate due date when term changes
  useEffect(() => {
    if (invoiceDate && term) {
      const date = new Date(invoiceDate);
      date.setDate(date.getDate() + parseInt(term));
      setDueDate(date.toISOString().split('T')[0]);
    }
  }, [invoiceDate, term]);

  // Fetch customers on mount
  useEffect(() => {
    dispatch(fetchCustomers({ page: 1, limit: 20 }));
  }, [dispatch]);

  // Cleanup search timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Handle click outside, escape, and dropdown viewport visibility
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      const projectButton = document.querySelector('[data-testid="project-dropdown-button-edit"]');
      if (projectButton?.contains(target)) {
        return;
      }

      if (
        showProjectList &&
        projectSelectorWrapperRef.current &&
        !projectSelectorWrapperRef.current.contains(target)
      ) {
        setShowProjectList(false);
      }

      const customerButton = document.querySelector('[data-testid="customer-dropdown-button-edit"]');
      if (customerButton?.contains(target)) {
        return;
      }

      if (
        showCustomerList &&
        customerDropdownRef.current &&
        !customerDropdownRef.current.contains(target)
      ) {
        setShowCustomerList(false);
      }
    };

    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowProjectList(false);
        setShowCustomerList(false);
      }
    };

    const ensureVisibleInViewport = (element: HTMLElement, topGap = 90, bottomGap = 32) => {
      const rect = element.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const maxBottom = viewportHeight - bottomGap;

      if (rect.bottom > maxBottom) {
        window.scrollBy({
          top: rect.bottom - maxBottom,
          behavior: 'smooth',
        });
        return;
      }

      if (rect.top < topGap) {
        window.scrollBy({
          top: rect.top - topGap,
          behavior: 'smooth',
        });
      }
    };

    if (showCustomerList && customerDropdownRef.current) {
      setTimeout(() => {
        customerDropdownRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);
    }

    if (showProjectList && projectDropdownPanelRef.current) {
      setTimeout(() => {
        if (projectDropdownPanelRef.current) {
          ensureVisibleInViewport(projectDropdownPanelRef.current, 90, 32);
        }
      }, 120);
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscKey);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [showProjectList, showCustomerList]);

  // Fetch projects when customer changes
  useEffect(() => {
    if (selectedCustomer) {
      console.log('🔄 Fetching projects for customer:', selectedCustomer);
      dispatch(clearCustomerProjects());
      
      // Find customer name from customers list, searchResults, or current invoice
      const customer = customers.find(c => c.id === selectedCustomer) 
        || searchResults.find(c => c.id === selectedCustomer);
      const customerName = customer?.companyName || currentInvoice?.billedTo?.companyName || '';
      
      console.log('📋 Customer name for projects:', customerName);
      dispatch(fetchCustomerProjects({ 
        customerId: selectedCustomer, 
        customerName: customerName,
        page: 1, 
        limit: 50 
      }));
      setProjectPage(1);
    }
  }, [selectedCustomer, dispatch, customers, searchResults, currentInvoice]);

  // Get selected company
  const selectedCompany = billedCompanies.find(company => company.id === selectedCompanyId) || billedCompanies[1];

  // Update project hsnSac
  const updateProjectField = (projectId: string, field: 'hsnSac', value: string) => {
    setSelectedProjects(prev =>
      prev.map(p => (p.id === projectId ? { ...p, [field]: value } : p))
    );
  };

  // Get displayed customers (search results or regular list)
  const displayedCustomers = customerSearchQuery.trim() ? searchResults : customers;

  // Get selected customer data - use stored data, or find in lists, or fall back to invoice data
  const selectedCustomerData = selectedCustomerFullData || displayedCustomers.find(c => c.id === selectedCustomer) || currentInvoice?.billedTo;

  // Handle customer selection - show modal if customer is changing
  const handleCustomerSelect = (customerId: string) => {
    // If same customer, just close dropdown
    if (customerId === selectedCustomer) {
      setShowCustomerList(false);
      return;
    }

    // Find the customer data now (while we still have it in displayedCustomers)
    const customer = displayedCustomers.find(c => c.id === customerId);

    // If projects are already selected and customer is changing, show confirmation modal
    if (selectedProjects.length > 0 && selectedCustomer) {
      setPendingCustomerId(customerId);
      setPendingCustomerData(customer); // Store the customer data for later
      setShowCustomerChangeModal(true);
      setShowCustomerList(false);
      return;
    }

    // No projects selected, proceed with change
    confirmCustomerChange(customerId, customer);
  };

  // Confirm customer change (called from modal or directly)
  const confirmCustomerChange = (customerId: string, customerData?: any) => {
    console.log('🔄 Customer changed to:', customerId);
    
    // Use provided customer data, or pendingCustomerData (from modal), or try to find it
    const customer = customerData || pendingCustomerData || displayedCustomers.find(c => c.id === customerId);
    
    // Store the selected customer's full data
    setSelectedCustomerFullData(customer);
    setSelectedCustomer(customerId);
    setSelectedProjects([]); // Clear selected projects
    setShowCustomerList(false);
    setShowCustomerChangeModal(false);
    setPendingCustomerId('');
    setPendingCustomerData(null);

    // Clear search
    setCustomerSearchQuery('');
    setIsSearchingCustomers(false);

    // Update tax type based on new customer
    const customerGSTIN = customer?.gstNumber || '';
    if (customerGSTIN && selectedCompany.gstin) {
      const newTaxType = determineTaxType(selectedCompany.gstin, customerGSTIN);
      setTaxType(newTaxType);

      const companyState = getStateFromGSTIN(selectedCompany.gstin);
      const customerState = getStateFromGSTIN(customerGSTIN);

      console.log(`🔍 Tax Type Auto-Detection:
        Company: ${selectedCompany.name}
        Company GSTIN: ${selectedCompany.gstin} (${companyState})
        Customer: ${customer?.companyName}
        Customer GSTIN: ${customerGSTIN} (${customerState})
        Tax Type: ${newTaxType} ${newTaxType === 'CGST_SGST' ? '(Same State - Intra-state)' : '(Different State - Inter-state)'}
      `);

      toast.success(`Tax type set to ${newTaxType === 'IGST' ? 'IGST (Inter-state)' : 'CGST + SGST (Intra-state)'}`);
    }

    // Clear customer projects and fetch new ones
    dispatch(clearCustomerProjects());
    setProjectPage(1);
    setHasMoreProjects(true);

    // Fetch projects for the selected customer
    if (customer) {
      console.log('Fetching projects for customer:', customer.companyName);
      dispatch(fetchCustomerProjects({
        customerId: customer.id,
        customerName: customer.companyName,
        page: 1,
        limit: 50
      }))
        .unwrap()
        .then((response) => {
          console.log('Customer projects fetched:', response);
          if (response?.pagination) {
            setHasMoreProjects(response.pagination.page < response.pagination.totalPages);
          }
        })
        .catch((error) => {
          console.error('Error fetching customer projects:', error);
          toast.error('Failed to load projects for this customer');
        });
    }
  };

  // Cancel customer change
  const cancelCustomerChange = () => {
    setShowCustomerChangeModal(false);
    setPendingCustomerId('');
    setPendingCustomerData(null);
  };

  // Handle project toggle (select/deselect)
  const handleProjectToggle = (project: any) => {
    const isSelected = selectedProjects.some(p => p.id === project.id);
    
    if (isSelected) {
      setSelectedProjects(prev => prev.filter(p => p.id !== project.id));
    } else {
      setSelectedProjects(prev => [...prev, {
        id: project.id,
        projectName: project.projectName,
        projectNumber: project.projectNumber,
        manakProjectId: project.manakProjectId,
        quantity: 1,
        rate: project.projectAmount || 0,
        hsnSac: '',
        paymentReceived: false
      }]);
    }
  };

  // Handle select all projects
  const handleSelectAllProjects = () => {
    if (selectedProjects.length === customerProjects.length) {
      setSelectedProjects([]);
    } else {
      const projectsWithDefaults = customerProjects.map(project => ({
        id: project.id,
        projectName: project.projectName,
        projectNumber: project.projectNumber,
        manakProjectId: project.manakProjectId,
        quantity: 1,
        rate: project.projectAmount || 0,
        hsnSac: '',
        paymentReceived: false
      }));
      setSelectedProjects(projectsWithDefaults);
    }
  };

  // Handle clear all selected projects
  const handleClearAllProjects = () => {
    setSelectedProjects([]);
  };

  // Ask confirmation before removing a project (Super Admin only)
  const requestProjectRemoval = (projectId: string) => {
    if (!isSuperAdmin) {
      toast.error('Only super admins can remove projects from an invoice');
      return;
    }

    const projectToRemove = selectedProjects.find(project => project.id === projectId);
    if (!projectToRemove) {
      return;
    }

    setPendingProjectRemoval(projectToRemove);
    setShowProjectRemovalModal(true);
  };

  const confirmProjectRemoval = () => {
    if (!pendingProjectRemoval) {
      return;
    }

    setSelectedProjects(prev => prev.filter(project => project.id !== pendingProjectRemoval.id));
    setShowProjectRemovalModal(false);
    setPendingProjectRemoval(null);
  };

  const cancelProjectRemoval = () => {
    setShowProjectRemovalModal(false);
    setPendingProjectRemoval(null);
  };

  // Customer search handler
  const handleCustomerSearch = (query: string) => {
    setCustomerSearchQuery(query);
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // If query is empty, just clear search results
    if (query.trim().length === 0) {
      setIsSearchingCustomers(false);
      return;
    }

    setIsSearchingCustomers(true);

    // Set new timeout for debounced search (300ms delay)
    searchTimeoutRef.current = setTimeout(() => {
      console.log('Searching customers for:', query.trim());
      dispatch(searchCustomers(query.trim()))
        .unwrap()
        .then(result => {
          console.log('Customer search results:', result);
        })
        .catch(error => {
          console.error('Customer search error:', error);
          toast.error('Error searching customers');
        })
        .finally(() => {
          setIsSearchingCustomers(false);
        });
    }, 300);
  };

  // Handle customer scroll for lazy loading
  const handleCustomerScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (customerSearchQuery) return; // Don't lazy load during search
    
    const target = e.currentTarget;
    const scrollPosition = target.scrollTop + target.clientHeight;
    const scrollHeight = target.scrollHeight;

    if (scrollHeight - scrollPosition < 100 && !isLoadingMoreCustomers && hasMoreCustomers) {
      setIsLoadingMoreCustomers(true);
      const nextPage = customerPage + 1;
      setCustomerPage(nextPage);
      dispatch(fetchCustomers({ page: nextPage, limit: 20 }))
        .unwrap()
        .then((response) => {
          if (response?.pagination) {
            setHasMoreCustomers(response.pagination.currentPage < response.pagination.totalPages);
          }
        })
        .finally(() => setIsLoadingMoreCustomers(false));
    }
  };

  // Handle project scroll for lazy loading
  const handleProjectScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const scrollPosition = target.scrollTop + target.clientHeight;
    const scrollHeight = target.scrollHeight;

    if (scrollHeight - scrollPosition < 100 && !isLoadingMoreProjects && hasMoreProjects && selectedCustomer) {
      setIsLoadingMoreProjects(true);
      const nextPage = projectPage + 1;
      setProjectPage(nextPage);

      const customer = displayedCustomers.find(c => c.id === selectedCustomer);
      dispatch(fetchCustomerProjects({
        customerId: selectedCustomer,
        customerName: customer?.companyName || '',
        page: nextPage,
        limit: 50
      }))
        .unwrap()
        .then((response) => {
          if (response?.pagination) {
            setHasMoreProjects(response.pagination.page < response.pagination.totalPages);
          }
        })
        .finally(() => setIsLoadingMoreProjects(false));
    }
  };

  // Toggle payment received for a specific project
  const togglePaymentReceived = (projectId: string) => {
    setSelectedProjects(prev => {
      const updated = prev.map(p => {
        if (p.id === projectId) {
          const newValue = !p.paymentReceived;
          console.log(`🔄 Toggling payment received for project ${p.projectName} (${p.id}): ${p.paymentReceived} → ${newValue}`);
          return { ...p, paymentReceived: newValue };
        }
        return p;
      });
      console.log(`📊 Updated projects state:`, updated.map(p => ({ id: p.id, name: p.projectName, paymentReceived: p.paymentReceived })));
      return updated;
    });
  };

  // Mark all projects as payment received or not
  const handleMarkAllPaymentReceived = (checked: boolean) => {
    console.log(`🎯 Mark All Payment Received: ${checked ? 'CHECKED' : 'UNCHECKED'}`);
    setSelectedProjects(prev => {
      const updated = prev.map(p => {
        // Don't change projects that were already payment received
        if (initialPaymentReceivedProjects.has(p.id)) {
          console.log(`  ⏭️  Skipping ${p.projectName} (${p.id}) - already marked as payment received initially`);
          return p;
        }
        console.log(`  ${checked ? '✅' : '❌'} ${p.projectName} (${p.id}): ${p.paymentReceived} → ${checked}`);
        return { ...p, paymentReceived: checked };
      });
      console.log(`📊 Updated projects state:`, updated.map(p => ({ id: p.id, name: p.projectName, paymentReceived: p.paymentReceived })));
      return updated;
    });
  };

  // Calculate totals
  const subtotal = selectedProjects.reduce((sum, project) => {
    const qty = 1; // Fixed quantity
    const rate = project.rate || 0;
    return sum + (qty * rate);
  }, 0);

  const discountAmount = 0; // Discount is always 0
  const subtotalAfterDiscount = subtotal;

  // Calculate tax based on type
  const taxAmount = (subtotalAfterDiscount * taxPercentage) / 100;
  const cgstAmount = taxType === 'CGST_SGST' ? taxAmount / 2 : 0;
  const sgstAmount = taxType === 'CGST_SGST' ? taxAmount / 2 : 0;
  const igstAmount = taxType === 'IGST' ? taxAmount : 0;

  const grandTotal = subtotalAfterDiscount + taxAmount;
  const balanceDue = grandTotal - amountPaid;

  // Debug function to log API payload
  const handleDebugPayload = () => {
    const apiPayload = {
      invoiceId,
      invoiceDate,
      term,
      dueDate,
      purchaseOrder,
      discount: Math.round(discountAmount),
      tax: Math.round(taxAmount),
      amountPaid: Math.round(amountPaid),
      notes,
      termsAndConditions: termsConditions,
      items: selectedProjects.map(project => ({
        id: project.id,
        projectId: project.id,
        projectName: project.projectName,
        projectNumber: project.projectNumber,
        quantity: project.quantity || 1,
        rate: project.rate || 0,
        hsnSac: project.hsnSac,
        lineTotal: (project.quantity || 1) * (project.rate || 0),
      })),
      subtotal,
      discountAmount,
      taxPercentage,
      taxAmount,
      grandTotal,
      balanceDue,
    };

    console.group('🐛 DEBUG: Update Invoice Payload');
    console.log('📋 Complete API Payload:', apiPayload);
    console.log('');
    console.log('📊 Financial Summary:');
    console.log('  Subtotal:', subtotal.toLocaleString('en-IN', { style: 'currency', currency: 'INR' }));
    console.log('  Discount:', discountAmount.toLocaleString('en-IN', { style: 'currency', currency: 'INR' }));
    console.log('  Tax:', taxAmount.toLocaleString('en-IN', { style: 'currency', currency: 'INR' }), `(${taxPercentage}%)`);
    console.log('  Amount Paid:', amountPaid.toLocaleString('en-IN', { style: 'currency', currency: 'INR' }));
    console.log('  Grand Total:', grandTotal.toLocaleString('en-IN', { style: 'currency', currency: 'INR' }));
    console.log('  Balance Due:', balanceDue.toLocaleString('en-IN', { style: 'currency', currency: 'INR' }));
    console.groupEnd();

    toast.success('Payload logged to console! Check browser developer tools (F12)', {
      duration: 4000,
    });
  };

  // Handle preview invoice
  const handlePreviewInvoice = async () => {
    if (selectedProjects.length === 0) {
      toast.error('No projects found in this invoice');
      return;
    }

    try {
      const invoiceData = {
        invoiceNumber: currentInvoice?.formattedInvoiceNumber || '',
        invoiceDate: invoiceDate,
        dueDate: dueDate,
        purchaseOrder: purchaseOrder,
        billedFrom: {
          name: selectedCompany.name,
          logo: selectedCompany.logo,
          addressLines: selectedCompany.addressLines,
          email: selectedCompany.email,
          phone: selectedCompany.phone,
          gstin: selectedCompany.gstin,
          cin: selectedCompany.cin,
        },
        billedTo: {
          companyName: currentInvoice?.billedTo?.companyName || '',
          ownerName: currentInvoice?.billedTo?.ownerName,
          address: currentInvoice?.billedTo?.clientAddress?.address,
          city: currentInvoice?.billedTo?.clientAddress?.city,
          pinCode: currentInvoice?.billedTo?.clientAddress?.pinCode,
          state: currentInvoice?.billedTo?.clientAddress?.state,
          country: currentInvoice?.billedTo?.clientAddress?.country,
          email: currentInvoice?.billedTo?.businessEmail || currentInvoice?.billedTo?.billingEmail || '',
          phone: currentInvoice?.billedTo?.phone,
          mobile: currentInvoice?.billedTo?.mobile,
          gstNumber: currentInvoice?.billedTo?.gstNumber,
        },
        items: selectedProjects.map(project => ({
          description: `${project.projectName || 'Unnamed Project'}\n${project.projectNumber || 'No project number'}`,
          hsnSac: project.hsnSac || '998391',
          quantity: project.quantity || 1,
          rate: project.rate || 0,
          amount: (project.quantity || 1) * (project.rate || 0),
        })),
        subtotal: subtotal,
        discountAmount: discountAmount,
        taxPercentage: taxPercentage,
        taxAmount: taxAmount,
        grandTotal: grandTotal,
        balanceDue: balanceDue,
        notes: notes,
        termsConditions: termsConditions,
      };

      await generateInvoicePDF(invoiceData, 'download');
      toast.success('Invoice preview downloaded successfully');
    } catch (error) {
      console.error('Error generating invoice preview:', error);
      toast.error('Failed to generate invoice preview');
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!invoiceId) {
      toast.error('Invoice ID is missing');
      return;
    }

    if (selectedProjects.length === 0) {
      toast.error('Invoice must have at least one project');
      return;
    }

    try {
      const payload: InvoiceUpdatePayload = {
        invoiceDate,
        term: term || undefined,
        dueDate: dueDate || undefined,
        purchaseOrder: purchaseOrder || undefined,
        discount: 0, // Always 0 - discount removed from UI
        amountPaid: Math.round(amountPaid),
        notes: notes || undefined,
        termsAndConditions: termsConditions || undefined,
        billedToCustomerId: selectedCustomer, // Include customer ID for change detection
        items: selectedProjects.map(project => ({
          projectId: project.id,
          quantity: 1, // Always 1 - quantity field removed from UI
          hsnSac: project.hsnSac || undefined,
          paymentReceived: project.paymentReceived === true,
        })),
      };

      // Add tax fields based on type
      if (taxType === 'IGST') {
        payload.tax = Math.round(taxAmount);
      } else if (taxType === 'CGST_SGST') {
        payload.cgst = Math.round(cgstAmount);
        payload.sgst = Math.round(sgstAmount);
      }

      console.log('Updating invoice with payload:', payload);
      console.log('Payment Received Status for each project:');
      selectedProjects.forEach(p => {
        console.log(`  - ${p.projectName} (${p.id}): ${p.paymentReceived ? '✅ YES' : '❌ NO'}`);
      });
      console.log('\n📋 Items in payload with payment received status:');
      payload.items.forEach((item, index) => {
        console.log(`  ${index + 1}. ProjectId: ${item.projectId}, PaymentReceived: ${item.paymentReceived}`);
      });
      console.log(`\n💰 Total items marked for payment: ${payload.items.filter(i => i.paymentReceived).length}`);

      await dispatch(updateInvoice({ invoiceId, data: payload })).unwrap();
      toast.success('Invoice updated successfully!');
      navigate('/billing-history');
    } catch (error) {
      console.error('Error updating invoice:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to update invoice');
    }
  };

  if (invoiceLoading && !currentInvoice) {
    return (
      <ComponentWrapper
        title={
          <div className="flex items-center gap-3">
            <MdOutlineDocumentScanner />
            <span>Edit Invoice</span>
          </div>
        }
      >
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500">Loading invoice...</div>
        </div>
      </ComponentWrapper>
    );
  }

  return (
    <ComponentWrapper
      title={
        <div className="flex items-center gap-3">
          <MdOutlineDocumentScanner />
          <span>Edit Invoice</span>
        </div>
      }
      buttonOne={
        <button
          type="button"
          onClick={handleDebugPayload}
          className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg font-semibold hover:bg-purple-200 transition-colors border-2 border-purple-300"
          title="Debug: Log payload to console"
        >
          <MdBugReport className="text-xl" />
          <span className="hidden sm:inline">Debug</span>
        </button>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Company Information */}
        <div className="font-inter">
          <div className="grid gap-8 md:grid-cols-2 md:gap-12">
            <div className="space-y-5">
              <span className="text-xs font-semibold uppercase tracking-[0.3em] text-gray-500">Billed From</span>

              {/* Company Name (Disabled) */}
              <div className="text-3xl font-bold text-gray-400 cursor-not-allowed opacity-60">
                {selectedCompany.name}
              </div>

              {/* Company Details */}
              <div className="text-gray-400 text-sm font-normal leading-6">
                {selectedCompany.addressLines.map((line, index) => (
                  <div key={index}>{line}</div>
                ))}
              </div>
              <div className="flex gap-4 items-center text-sm font-normal text-gray-400">
                <span>{selectedCompany.email}</span>
                <span className="text-gray-300">|</span>
                <span>{selectedCompany.phone}</span>
              </div>
              <div className="text-sm font-normal text-gray-400">
                <div><span className='font-medium'>GSTIN</span> - {selectedCompany.gstin}</div>
                {selectedCompany.cin && (
                  <div><span className='font-medium'>CIN No</span> - {selectedCompany.cin}</div>
                )}
              </div>
            </div>

            <div className="flex flex-col items-start gap-2 md:items-end md:justify-end">
              <span className="text-xs font-semibold uppercase tracking-[0.3em] text-gray-500">Invoice Series</span>

              {/* Invoice Series (Disabled) */}
              <div className="flex gap-3 items-center opacity-60 cursor-not-allowed">
                <div className={`px-4 py-2 rounded-lg font-semibold text-lg ${invoiceSeries === 'MESPL'
                  ? 'bg-gray-300 text-gray-600'
                  : 'bg-gray-100 text-gray-500'
                  }`}>
                  MESPL
                </div>
                <div className={`px-4 py-2 rounded-lg font-semibold text-lg ${invoiceSeries === 'MESS'
                  ? 'bg-gray-300 text-gray-600'
                  : 'bg-gray-100 text-gray-500'
                  }`}>
                  MESS
                </div>
              </div>

              {/* Invoice Number (Disabled) */}
              <div className="mt-3">
                <div className="flex flex-col gap-1">
                  <span className="text-3xl font-bold text-gray-400 cursor-not-allowed">
                    {currentInvoice?.formattedInvoiceNumber || 'Loading...'}
                  </span>
                  <span className="text-xs text-gray-400">Invoice number (cannot be changed)</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Customer and Invoice Details */}
        <div className="bg-white rounded-lg pt-10">
          <div className="flex gap-14 items-start">
            {/* LEFT: BILL TO SECTION */}
            <div className="w-2/5">
              <div className="text-xs font-semibold uppercase tracking-[0.3em] text-gray-500 mb-3">BILLED TO</div>

              {/* Customer Selection Dropdown - Matching CreateInvoice Style */}
              <div className="relative">
                <button
                  type="button"
                  data-testid="customer-dropdown-button-edit"
                  onClick={() => setShowCustomerList(!showCustomerList)}
                  className="flex items-center gap-2 text-3xl font-bold text-[#E14732] hover:text-red-700 transition-colors"
                >
                  <span>{selectedCustomerData?.companyName || currentInvoice?.billedTo?.companyName || 'Select Customer'}</span>
                  <HiChevronDown className="text-xl mt-1" />
                </button>

                {/* Customer Dropdown List - Same as CreateInvoice */}
                {showCustomerList && (
                  <div ref={customerDropdownRef} className="absolute top-full left-0 mt-2 w-full max-w-md bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                    {/* Search Input - Sticky at top */}
                    <div className="sticky top-0 bg-white border-b border-gray-200 p-3 z-10">
                      <div className="relative">
                        <input
                          type="text"
                          value={customerSearchQuery}
                          onChange={(e) => handleCustomerSearch(e.target.value)}
                          placeholder="Search customer by company name..."
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E14732] focus:border-[#E14732]"
                          autoFocus
                        />
                        <svg
                          className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                          />
                        </svg>
                        {isSearchingCustomers && (
                          <svg
                            className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin h-4 w-4 text-[#E14732]"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        )}
                      </div>
                    </div>

                    {/* Scrollable Customer List */}
                    <div
                      ref={customerScrollRef}
                      onScroll={handleCustomerScroll}
                      className="max-h-60 overflow-y-auto"
                    >
                      {displayedCustomers.length > 0 ? (
                        <>
                          {displayedCustomers.map(customer => (
                            <button
                              key={customer.id}
                              type="button"
                              onClick={() => handleCustomerSelect(customer.id)}
                              className={`w-full text-left px-4 py-3 hover:bg-red-50 transition-colors border-b last:border-b-0 ${
                                selectedCustomer === customer.id ? 'bg-red-50' : ''
                              }`}
                            >
                              <div className="font-semibold text-gray-800">{customer.companyName}</div>
                              <div className="text-xs text-gray-500 mt-1">{customer.businessEmail || customer.billingEmail}</div>
                            </button>
                          ))}
                          {/* Loading indicator for lazy loading */}
                          {!customerSearchQuery && isLoadingMoreCustomers && (
                            <div className="px-4 py-3 text-center text-sm text-gray-500">
                              <div className="flex items-center justify-center gap-2">
                                <svg className="animate-spin h-4 w-4 text-[#E14732]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Loading more...
                              </div>
                            </div>
                          )}
                          {/* End of list indicator */}
                          {!customerSearchQuery && !hasMoreCustomers && customers.length > 10 && (
                            <div className="px-4 py-2 text-center text-xs text-gray-400">
                              All customers loaded
                            </div>
                          )}
                        </>
                      ) : (isSearchingCustomers || searching) ? (
                        <div className="px-4 py-6 text-center text-sm text-gray-500">
                          <div className="flex items-center justify-center gap-2">
                            <svg className="animate-spin h-4 w-4 text-[#E14732]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            {customerSearchQuery ? 'Searching...' : 'Loading customers...'}
                          </div>
                        </div>
                      ) : customerSearchQuery ? (
                        <div className="px-4 py-6 text-center text-sm text-gray-500">
                          No customers found for "{customerSearchQuery}"
                        </div>
                      ) : (
                        <div className="px-4 py-3 text-sm text-gray-500">No customers available</div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Customer Details */}
              {selectedCustomerData && (
                <>
                  <div className="text-gray-500 mt-4 text-sm font-normal leading-6">
                    {selectedCustomerData.ownerName && (
                      <div>{selectedCustomerData.ownerName}</div>
                    )}
                    {selectedCustomerData.clientAddress && (
                      <>
                        <div className="mt-1">{selectedCustomerData.clientAddress.address}</div>
                        <div>
                          {selectedCustomerData.clientAddress.city} - {selectedCustomerData.clientAddress.pinCode}, {selectedCustomerData.clientAddress.state}, {selectedCustomerData.clientAddress.country}
                        </div>
                      </>
                    )}
                  </div>
                  <div className="flex gap-4 mt-4 items-center text-sm text-gray-500">
                    <span>{selectedCustomerData.businessEmail || selectedCustomerData.billingEmail}</span>
                    {(selectedCustomerData.phone || selectedCustomerData.mobile) && (
                      <>
                        <span className="text-gray-400">|</span>
                        <span>{selectedCustomerData.phone || selectedCustomerData.mobile}</span>
                      </>
                    )}
                  </div>
                  {selectedCustomerData.gstNumber && (
                    <div className="text-sm text-gray-500 mt-2">
                      <span className="font-medium">GSTIN</span> - {selectedCustomerData.gstNumber}
                    </div>
                  )}
                </>
              )}

              {/* Project Selection Dropdown (Compact) */}
              <div ref={projectSelectorWrapperRef} className="relative mt-5">
                <button
                  type="button"
                  data-testid="project-dropdown-button-edit"
                  onClick={() => {
                    if (!selectedCustomer && !currentInvoice?.billedTo?.id) {
                      toast.error('Please select a customer first');
                      return;
                    }
                    setShowProjectList(!showProjectList);
                  }}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border transition-all ${showProjectList
                    ? 'bg-[#E14732] text-white border-[#E14732]'
                    : 'bg-white text-[#E14732] border-[#E14732] hover:bg-red-50'
                    }`}
                >
                  <div className="flex items-center gap-2.5">
                    <MdOutlineDocumentScanner className={`text-lg ${showProjectList ? 'text-white' : 'text-[#E14732]'}`} />
                    <span className="font-semibold text-base">
                      {selectedProjects.length > 0
                        ? `${selectedProjects.length} Project${selectedProjects.length > 1 ? 's' : ''} Selected`
                        : 'Add Project'}
                    </span>
                  </div>
                  <HiChevronDown className={`text-lg ${showProjectList ? 'text-white rotate-180' : 'text-[#E14732]'} transition-transform`} />
                </button>

                <AnimatePresence>
                  {showProjectList && (
                    <motion.div
                      ref={projectDropdownPanelRef}
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.2, ease: "easeOut" }}
                      className="relative mt-2 w-full bg-white border border-[#E14732] rounded-lg shadow-sm overflow-hidden"
                      style={{ maxHeight: '320px' }}
                    >
                      <div
                        ref={projectDropdownRef}
                        onScroll={handleProjectScroll}
                        className="overflow-y-auto project-selector-scroll"
                        style={{ maxHeight: '320px', scrollbarGutter: 'stable' }}
                      >
                        {customerProjects && customerProjects.length > 0 ? (
                          <>
                            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-2.5 flex items-center justify-between z-10">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={selectedProjects.length === customerProjects.length && customerProjects.length > 0}
                                  onChange={handleSelectAllProjects}
                                  className="w-4 h-4 text-[#E14732] border-gray-300 rounded focus:ring-[#E14732]"
                                />
                                <span className="font-semibold text-sm text-gray-700">SELECT ALL</span>
                              </label>
                              <button
                                type="button"
                                onClick={handleClearAllProjects}
                                className="text-[#E14732] text-sm font-semibold hover:underline"
                              >
                                CLEAR
                              </button>
                            </div>

                            {customerProjects.map((project) => (
                              <label
                                key={project.id}
                                className="flex items-center gap-3 px-4 py-3 hover:bg-red-50 cursor-pointer border-b last:border-b-0 transition-colors"
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedProjects.some(p => p.id === project.id)}
                                  onChange={() => handleProjectToggle(project)}
                                  className="w-4 h-4 text-[#E14732] border-gray-300 rounded focus:ring-[#E14732]"
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="font-semibold text-sm text-gray-900 truncate">{project.projectName || 'Unnamed Project'}</div>
                                  <div className="text-xs text-gray-500 mt-0.5">{"M" + project.manakProjectId || 'No MID'}</div>
                                </div>
                              </label>
                            ))}

                            {isLoadingMoreProjects && (
                              <div className="px-4 py-3 text-center">
                                <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
                                  <svg className="animate-spin h-4 w-4 text-[#E14732]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                  Loading more projects...
                                </div>
                              </div>
                            )}

                            {!hasMoreProjects && customerProjects.length > 10 && (
                              <div className="px-4 py-2 text-center text-xs text-gray-400">
                                All projects loaded
                              </div>
                            )}
                          </>
                        ) : projectsLoading ? (
                          <div className="px-4 py-6 text-center">
                            <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                              <svg className="animate-spin h-4 w-4 text-[#E14732]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Loading projects...
                            </div>
                          </div>
                        ) : selectedCustomer ? (
                          <div className="px-4 py-6 text-center text-sm text-gray-500">
                            No projects found for this customer
                          </div>
                        ) : (
                          <div className="px-4 py-6 text-center text-sm text-gray-500">
                            Select a customer to view available projects
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* RIGHT: INVOICE SECTION */}
            <div className="flex-1 flex flex-col gap-6">
              {/* Invoice Date row */}
              <div className="flex justify-end">
                <div className="flex items-center border border-gray-100 rounded overflow-hidden w-fit">
                  <label className="text-base text-gray-500 italic font-semibold bg-gray-100 px-5 py-2.5 whitespace-nowrap">
                    Invoice Date
                  </label>
                  <input
                    type="date"
                    value={invoiceDate}
                    onChange={(e) => setInvoiceDate(e.target.value)}
                    className="bg-white px-5 py-2.5 font-semibold text-gray-600 text-base focus:outline-none cursor-pointer border-none min-w-[200px]"
                  />
                </div>
              </div>

              {/* Term Select row */}
              <div className="flex justify-end">
                <div className="flex items-center border border-gray-100 rounded overflow-hidden w-fit">
                  <label className="text-base text-gray-500 italic font-semibold bg-gray-100 px-5 py-2.5 whitespace-nowrap">
                    Term
                  </label>
                  <div className="relative">
                    <select
                      value={term}
                      onChange={(e) => setTerm(e.target.value)}
                      className="bg-white px-5 py-2.5 pr-10 text-gray-500 font-medium focus:outline-none appearance-none cursor-pointer border-none min-w-[200px]"
                    >
                      <option value="">Select Term</option>
                      {termOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <HiChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  </div>
                </div>
              </div>

              {/* Due Date */}
              <div className="flex justify-end">
                <div className="flex items-center border border-gray-100 rounded overflow-hidden w-fit">
                  <label className="text-base text-gray-500 italic font-semibold bg-gray-100 px-5 py-2.5 whitespace-nowrap">
                    Due Date
                  </label>
                  {dueDate ? (
                    <div className="bg-white px-5 py-2.5 font-semibold text-gray-500 text-base min-w-[200px]">
                      {new Date(dueDate).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      }).replace(/\//g, '-')}
                    </div>
                  ) : (
                    <div className="bg-white px-5 py-2.5 text-gray-400 italic flex items-center gap-2 min-w-[200px]">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      Note : Set Term First
                    </div>
                  )}
                </div>
              </div>

              {/* Purchase Order */}
              <div className="flex justify-end">
                <div className="flex items-center border border-gray-100 rounded overflow-hidden w-fit">
                  <label className="text-base text-gray-500 italic font-semibold bg-gray-100 px-5 py-2.5 whitespace-nowrap">
                    Purchase Order (P.O)
                  </label>
                  <input
                    className="bg-white px-5 py-2.5 text-gray-700 font-medium focus:outline-none border-none min-w-[200px]"
                    placeholder="Enter Purchase Order"
                    type="text"
                    value={purchaseOrder}
                    onChange={(e) => setPurchaseOrder(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Project Selection Section - Matching CreateInvoice Style */}
        <div className="bg-white rounded-lg pt-4">
          {/* Display Selected Projects - Rich Invoice Table */}
          {selectedProjects.length > 0 && (
            <div className="">
              {/* Table Header */}
              <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-red-50 rounded-t-lg border-gray-200 font-semibold text-[#E14732] text-sm">
                <div className="col-span-1 text-center">#</div>
                <div className="col-span-3">Item Details</div>
                <div className="col-span-2 text-center">HSN/SAC</div>
                <div className="col-span-2 text-center">Rate</div>
                <div className="col-span-2 text-right">Amount</div>
                <div className="col-span-1 text-center">Payment</div>
                <div className="col-span-1 text-center">Action</div>
              </div>

              {/* Project Rows */}
              {selectedProjects.map((project, index) => {
                const qty = 1; // Fixed quantity
                const rate = project.rate || 0;
                const amount = qty * rate;

                return (
                  <div
                    key={project.id}
                    className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-gray-100 hover:bg-gray-50 transition-colors items-center"
                  >
                    {/* Serial Number */}
                    <div className="col-span-1 text-center text-gray-600 font-medium">
                      {index + 1}
                    </div>

                    {/* Item Details */}
                    <div className="col-span-3">
                      <div className="font-semibold text-gray-900">{project.projectName || 'Unnamed Project'}</div>
                      <div className="text-sm text-gray-500 mt-1">{"M" + project.manakProjectId || 'No MID'}</div>
                    </div>

                    {/* HSN/SAC Input */}
                    <div className="col-span-2 flex justify-center">
                      <input
                        type="text"
                        value={project.hsnSac || ''}
                        onChange={(e) => updateProjectField(project.id, 'hsnSac', e.target.value)}
                        placeholder="998391"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-center focus:outline-none focus:ring-2 focus:ring-[#E14732] focus:border-[#E14732]"
                      />
                    </div>

                    {/* Rate Display (Read-only) */}
                    <div className="col-span-2 flex justify-center">
                      <div className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg text-right font-semibold text-gray-700 w-full text-center">
                        ₹{rate.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    </div>

                    {/* Amount */}
                    <div className="col-span-2 flex items-center justify-end">
                      <div className="font-semibold text-gray-900">
                        ₹{amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    </div>

                    {/* Payment Received Toggle */}
                    <div className="col-span-1 flex justify-center">
                      <label className={`relative inline-flex items-center ${initialPaymentReceivedProjects.has(project.id) ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
                        <input
                          type="checkbox"
                          checked={project.paymentReceived === true}
                          onChange={() => togglePaymentReceived(project.id)}
                          disabled={initialPaymentReceivedProjects.has(project.id)}
                          className="sr-only peer"
                        />
                        <div className={`w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500 ${initialPaymentReceivedProjects.has(project.id) ? 'peer-disabled:opacity-60 peer-disabled:cursor-not-allowed' : ''}`}></div>
                      </label>
                    </div>

                    {/* Delete Button */}
                    <div className="col-span-1 flex justify-center">
                      {isSuperAdmin ? (
                        <button
                          type="button"
                          onClick={() => requestProjectRemoval(project.id)}
                          disabled={selectedProjects.length <= 1}
                          className={`transition-colors ${selectedProjects.length <= 1
                              ? 'text-gray-300 cursor-not-allowed'
                              : 'text-[#E14732] hover:text-red-700'
                            }`}
                          title={
                            selectedProjects.length <= 1
                              ? 'At least one project must remain in the invoice'
                              : 'Remove project'
                          }
                        >
                          <HiTrash className="w-5 h-5" />
                        </button>
                      ) : (
                        <span
                          className="text-xs text-gray-400"
                          title="Only super admins can remove projects from an invoice"
                        >
                          -
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Totals Section */}
              <div className="mt-6 border-t border-gray-200 pt-6 space-y-4">
                {/* Subtotal */}
                <div className="flex items-center justify-between px-6 py-2">
                  <span className="font-semibold text-gray-700">Subtotal</span>
                  <span className="font-semibold text-gray-900 text-lg">
                    ₹{subtotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>

                {/* Tax Type Display */}
                <div className="flex items-center justify-between px-6 py-3 bg-blue-50 border-l-4 border-blue-500">
                  <div className="flex flex-col">
                    <span className="font-semibold text-gray-700">Tax Type</span>
                    <span className="text-xs text-gray-500 mt-1">
                      {taxType === 'IGST' ? 'Inter-state transaction' : 'Intra-state transaction (Same state)'}
                    </span>
                  </div>
                  <div className={`px-4 py-2 rounded-lg font-bold ${taxType === 'IGST'
                    ? 'bg-purple-100 text-purple-700 border-2 border-purple-300'
                    : 'bg-green-100 text-green-700 border-2 border-green-300'
                    }`}>
                    {taxType === 'IGST' ? 'IGST' : 'CGST + SGST'}
                  </div>
                </div>

                {/* Tax Breakdown Display */}
                {taxType === 'IGST' ? (
                  <div className="flex items-center justify-between px-6 py-2 bg-gray-50">
                    <span className="font-semibold text-gray-700">IGST ({taxPercentage}%)</span>
                    <span className="font-semibold text-gray-700">
                      ₹{igstAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between px-6 py-2 bg-gray-50">
                      <span className="font-semibold text-gray-700">CGST ({(taxPercentage / 2).toFixed(2)}%)</span>
                      <span className="font-semibold text-gray-700">
                        ₹{cgstAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="flex items-center justify-between px-6 py-2 bg-gray-50">
                      <span className="font-semibold text-gray-700">SGST ({(taxPercentage / 2).toFixed(2)}%)</span>
                      <span className="font-semibold text-gray-700">
                        ₹{sgstAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  </>
                )}

                {/* Amount Paid */}
                <div className="flex items-center justify-between px-6 py-2">
                  <span className="font-semibold text-gray-700">Amount Paid</span>
                  <div className="relative w-40">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={amountPaid}
                      onChange={(e) => setAmountPaid(parseFloat(e.target.value) || 0)}
                      onWheel={(e) => e.currentTarget.blur()}
                      placeholder="0.00"
                      className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg text-right focus:outline-none focus:ring-2 focus:ring-[#E14732] focus:border-[#E14732]"
                    />
                  </div>
                </div>

                {/* Grand Total */}
                <div className="flex items-center justify-between px-6 py-4 mt-4 border-t border-gray-300">
                  <span className="text-xl font-bold text-gray-900">Grand Total</span>
                  <span className="text-2xl font-bold text-[#E14732]">
                    ₹{grandTotal.toLocaleString('en-IN')}
                  </span>
                </div>

                {/* Mark All Payment Received */}
                <div className="flex items-center justify-between px-6 py-3 bg-green-50 border-l-4 border-green-500">
                  <span className="font-semibold text-gray-700">Mark All Payment Received</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedProjects.filter(p => !initialPaymentReceivedProjects.has(p.id)).every(p => p.paymentReceived === true)}
                      onChange={(e) => handleMarkAllPaymentReceived(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-14 h-7 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-green-600"></div>
                  </label>
                </div>

                {/* Balance Due */}
                {/* <div className="flex items-center justify-between px-6 py-4 bg-yellow-50 border-l-4 border-yellow-500">
                  <span className="text-lg font-bold text-gray-900">Balance Due</span>
                  <span className="text-xl font-bold text-yellow-700">
                    ₹{balanceDue.toLocaleString('en-IN')}
                  </span>
                </div> */}
              </div>

              {/* Notes and Terms & Conditions */}
              <div className="grid md:grid-cols-2 gap-6 px-6 border-gray-200 mt-6">
                {/* Notes */}
                <div>
                  <label className="block font-semibold text-gray-700 mb-3">Notes</label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add any additional notes here..."
                    rows={3}
                    className="w-full px-4 py-3 text-sm text-gray-600 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E14732] focus:border-[#E14732] resize-none leading-relaxed"
                  />
                </div>

                {/* Terms & Conditions */}
                <div>
                  <label className="block font-semibold text-gray-700 mb-3">Terms & Conditions</label>
                  <textarea
                    value={termsConditions}
                    onChange={(e) => setTermsConditions(e.target.value)}
                    placeholder="Add terms and conditions here..."
                    rows={3}
                    className="w-full px-4 py-3 text-sm text-gray-600 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E14732] focus:border-[#E14732] resize-none leading-relaxed"
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="grid md:grid-cols-2 gap-4 mt-8">
                {/* See Preview Button */}
                <button
                  type="button"
                  onClick={handlePreviewInvoice}
                  className="flex items-center justify-center gap-3 px-6 py-8 bg-white border-2 border-green-500 text-green-600 rounded-lg font-semibold text-2xl hover:bg-green-50 transition-colors"
                >
                  <HiEye className="text-2xl" />
                  <span>See Preview</span>
                </button>

                {/* Update Invoice Button */}
                <button
                  type="submit"
                  disabled={invoiceLoading}
                  className="flex items-center justify-center gap-3 px-6 py-8 bg-[#E14732] text-white rounded-lg font-semibold text-2xl hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <HiOutlineDocumentText className="text-2xl" />
                  <span>{invoiceLoading ? 'Updating Invoice...' : 'Update Invoice'}</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </form>

      {/* Customer Change Confirmation Modal */}
      <AnimatePresence>
        {showCustomerChangeModal && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50"
              onClick={cancelCustomerChange}
            />
            
            {/* Centered Modal Box */}
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-full max-w-md bg-white shadow-xl rounded-xl flex flex-col overflow-hidden">
              {/* Modal Content */}
              <div className="flex-1 p-6 text-left">
                {/* Title */}
                <div className="mb-4">
                  <h2 className="text-xl font-bold text-gray-900 mb-1">
                    Change Customer?
                  </h2>
                  <p className="text-gray-500 text-sm leading-relaxed">
                    This will reset current projects and billing status.
                  </p>
                </div>
                
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-3">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0"></span>
                    <span className="text-sm text-gray-600">Removes selected projects</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0"></span>
                    <span className="text-sm text-gray-600">Clears billing status</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0"></span>
                    <span className="text-sm text-gray-600">Loads new customer data</span>
                  </li>
                </ul>
              </div>
              
              {/* Modal Footer */}
              <div className="p-6 pt-0 flex flex-col gap-3">
                <button
                  type="button"
                  onClick={() => confirmCustomerChange(pendingCustomerId, pendingCustomerData)}
                  className="w-full py-2.5 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors text-sm shadow-sm"
                >
                  Yes, Change Customer
                </button>
                <button
                  type="button"
                  onClick={cancelCustomerChange}
                  className="w-full py-2.5 border border-gray-200 text-gray-600 rounded-lg font-semibold hover:bg-gray-50 transition-colors text-sm"
                >
                  Cancel
                </button>
              </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Project Removal Confirmation Modal */}
      <AnimatePresence>
        {showProjectRemovalModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50"
              onClick={cancelProjectRemoval}
            />

            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-full max-w-md bg-white shadow-xl rounded-xl flex flex-col overflow-hidden">
                <div className="flex-1 p-6 text-left">
                  <div className="mb-4">
                    <h2 className="text-xl font-bold text-gray-900 mb-1">Remove Project?</h2>
                    <p className="text-gray-500 text-sm leading-relaxed">
                      This will remove this project from the current invoice.
                    </p>
                  </div>

                  <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-sm text-gray-700 font-medium">
                      {pendingProjectRemoval?.projectName || 'Selected Project'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {pendingProjectRemoval?.manakProjectId
                        ? `M${pendingProjectRemoval.manakProjectId}`
                        : 'No MID'}
                    </p>
                  </div>

                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start gap-3">
                      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0"></span>
                      <span className="text-sm text-gray-600">Removes this project from invoice items</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0"></span>
                      <span className="text-sm text-gray-600">Updates related billed/payment tracking on save</span>
                    </li>
                  </ul>
                </div>

                <div className="p-6 pt-0 flex flex-col gap-3">
                  <button
                    type="button"
                    onClick={confirmProjectRemoval}
                    className="w-full py-2.5 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition-colors text-sm shadow-sm"
                  >
                    Yes, Remove Project
                  </button>
                  <button
                    type="button"
                    onClick={cancelProjectRemoval}
                    className="w-full py-2.5 border border-gray-200 text-gray-600 rounded-lg font-semibold hover:bg-gray-50 transition-colors text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </ComponentWrapper>
  );
};

export default EditInvoice;
